/**
 * Core conversion functions for measurement units
 */

// Base conversion constants
const INCH_TO_MM = 25.4;
const INCH_TO_POINTS = 72;
const INCH_TO_PICAS = 6;
const POINTS_TO_PICAS = 1/12;

/**
 * Convert millimeters to inches
 * @param {number} mm - Value in millimeters
 * @returns {number} Value in inches
 */
export const mmToInches = (mm) => {
  return mm / INCH_TO_MM;
};

/**
 * Convert inches to millimeters
 * @param {number} inches - Value in inches
 * @returns {number} Value in millimeters
 */
export const inchesToMm = (inches) => {
  return inches * INCH_TO_MM;
};

/**
 * Convert points to inches
 * @param {number} points - Value in points
 * @returns {number} Value in inches
 */
export const pointsToInches = (points) => {
  return points / INCH_TO_POINTS;
};

/**
 * Convert inches to points
 * @param {number} inches - Value in inches
 * @returns {number} Value in points
 */
export const inchesToPoints = (inches) => {
  return inches * INCH_TO_POINTS;
};

/**
 * Convert picas to inches
 * @param {number} picas - Value in picas
 * @returns {number} Value in inches
 */
export const picasToInches = (picas) => {
  return picas / INCH_TO_PICAS;
};

/**
 * Convert inches to picas
 * @param {number} inches - Value in inches
 * @returns {number} Value in picas
 */
export const inchesToPicas = (inches) => {
  return inches * INCH_TO_PICAS;
};

/**
 * Convert points to picas
 * @param {number} points - Value in points
 * @returns {number} Value in picas
 */
export const pointsToPicas = (points) => {
  return points * POINTS_TO_PICAS;
};

/**
 * Convert picas to points
 * @param {number} picas - Value in picas
 * @returns {number} Value in points
 */
export const picasToPoints = (picas) => {
  return picas / POINTS_TO_PICAS;
};

/**
 * Convert pixels to inches at a specific PPI
 * @param {number} pixels - Value in pixels
 * @param {number} ppi - Pixels per inch
 * @returns {number} Value in inches
 */
export const pixelsToInches = (pixels, ppi) => {
  return pixels / ppi;
};

/**
 * Convert inches to pixels at a specific PPI
 * @param {number} inches - Value in inches
 * @param {number} ppi - Pixels per inch
 * @returns {number} Value in pixels (rounded to nearest integer)
 */
export const inchesToPixels = (inches, ppi) => {
  return Math.round(inches * ppi);
};

/**
 * Generic unit conversion function (convenience wrapper)
 * @param {number} value - The value to convert
 * @param {string} fromUnit - Source unit ('mm', 'inches', 'points', 'picas', 'pixels')
 * @param {string} toUnit - Target unit ('mm', 'inches', 'points', 'picas', 'pixels')
 * @param {number} ppi - Pixels per inch (required when converting to/from pixels)
 * @returns {number} The converted value
 */
export const convertUnit = (value, fromUnit, toUnit, ppi = 300) => {
  // Convert input to inches (base unit)
  let inches;
  
  switch(fromUnit) {
    case 'mm':
      inches = mmToInches(value);
      break;
    case 'inches':
      inches = value;
      break;
    case 'points':
      inches = pointsToInches(value);
      break;
    case 'picas':
      inches = picasToInches(value);
      break;
    case 'pixels':
      inches = pixelsToInches(value, ppi);
      break;
    default:
      throw new Error(`Unknown source unit: ${fromUnit}`);
  }
  
  // Convert inches to output unit
  switch(toUnit) {
    case 'mm':
      return inchesToMm(inches);
    case 'inches':
      return inches;
    case 'points':
      return inchesToPoints(inches);
    case 'picas':
      return inchesToPicas(inches);
    case 'pixels':
      return inchesToPixels(inches, ppi);
    default:
      throw new Error(`Unknown target unit: ${toUnit}`);
  }
};